import { CommonModule, KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, map, takeUntil, tap } from 'rxjs/operators';
import { AppService } from 'src/app/services/app.service';
import { ReportsService } from '../reports.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import {LocalizationService, LocalizedNumberFormat} from 'src/app/shared/localization/localization.service';
import { IClosedCasesRequest, IClosedCaseReportResult, CLOSED_CASE_SEARCH_RESULT_COLUMNS,} from '../reports-models';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { IPaginatedSearch } from 'src/app/shared/models';
import { LazyLoadEvent } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { Router } from '@angular/router';
import { Table, TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { DatePickerModule } from 'primeng/datepicker';
import { SettingsService } from "src/app/services/settings.service";


@Component({
  selector: 'app-closed-cases',
  templateUrl: './closed-cases.component.html',
  styleUrls: ['./closed-cases.component.scss'],
  imports: [CommonModule, TranslateModule, TableModule, DatePickerModule, FormsModule, CustomMultiSelectComponent]
})
export class ClosedCasesComponent implements OnInit {
  customerNumber$ = this.appService.customerNumber$;
  customers$: Observable<KeyValue<number, string>[]>;
  startDate: Date;
  endDate: Date;
  includeReminderCases = false;  
  numberFormat: LocalizedNumberFormat;
  dateFormat: string;
  numberOfRowsPerPage = 20;
  currentPageNumber = 1;
  totalNumberOfItems = 0;
  sortField: string = null;
  sortAscending: boolean = null;
  cols = [...CLOSED_CASE_SEARCH_RESULT_COLUMNS];
  selectedCustomers: number[] = [];
  closedCases: IClosedCaseReportResult[];
  totalCases: string;
  userAssignedCustomersCount: number;

  canSearch: boolean;
  showResults: boolean;
  isLoading: boolean;
  languageCode: string;

  @ViewChild('closedCasesTable') closedCasesTable: Table;

  private readonly unsubscribe$ = new Subject();

  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private localisationService: LocalizationService,
    private notifications: MessageToUiService,
    private spinnerService: GlobalSpinnerService,
    private translateService: TranslateService,
    private router: Router,
    private settingsService: SettingsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.spinnerService.show();
    this.initializeCustomerList();
    this.localisationService.settings$.subscribe(
      (_) => (this.numberFormat = _.numberPipeFormat)
    );

    this.settingsService.getSettingValueByName('AreRef2Ref3Inlcuded')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((areRef2Ref3Inlcuded) => {
        if (!areRef2Ref3Inlcuded.isTruthy()) {
          this.cols = this.cols.filter(
            col => col.name !== 'customerReference2' && col.name !== 'customerReference3'
          );
        }
      });

    this.localisationService.currentLanguage$.subscribe(
      _ => this.languageCode = _
    );
    this.spinnerService.hide();
  }

  private initializeCustomerList() {
    this.customers$ = this.appService.customers$.pipe(
      tap(_ => {
        this.userAssignedCustomersCount = _.length;
        if (this.userAssignedCustomersCount == 1)
          {this.selectedCustomers = [_[0].number]; }
      }),
      map((_) => _.map((x) => ({ key: x.number, value: `${x.number} - ${x.name}` })))
    );
  }

  onGenerateReport() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    if (this.startDate == undefined) {
      this.notifications.errorKey('Errors.Reports.StartDateNotSelected');
      return false;
    }
    if (this.endDate == undefined) {
      this.notifications.errorKey('Errors.Reports.EndDateNotSelected');
      return false;
    }

    const parameters = this.getPaginatedReportParameters();
    this.isLoading = true;

    this.reportsService.getClosedCasesReport(parameters)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }))
      .subscribe((
        casesList
      ) => {
        this.closedCases = casesList.pageItems;
        this.translateStatusText(this.languageCode);
        this.totalNumberOfItems = casesList.totalCount;
        this.totalCases = this.translateService.instant('Reports.ClosedCases.TotalCases') + casesList.totalCount.toString();
      });
  }
  private getPaginatedReportParameters(): IPaginatedSearch<IClosedCasesRequest> {
    return {
      currentPage: this.currentPageNumber,
      pageSize: this.numberOfRowsPerPage,
      filter: this.getReportParameters(),
      sortField: this.getSortField(this.sortField),
      sortAscending: this.sortAscending
    } as IPaginatedSearch<IClosedCasesRequest>;
  }

  private getReportParameters(): IClosedCasesRequest {
    const customerNumbers = this.selectedCustomers.join(',');
    const includeReminderCases = this.includeReminderCases;
    
    return {
      customerNumbers,
      startDate: this.startDate,
      endDate: this.endDate,
      includeReminderCases
    } as IClosedCasesRequest;
  }

  public onSelectedCustomersChange(event: number[]) {
    if (event.length == 0) {
      this.closedCases = null;
    }
  }

  onPageChange(event: LazyLoadEvent) {
    if (event.rows != this.numberOfRowsPerPage)
      {this.numberOfRowsPerPage = event.rows; }
    this.currentPageNumber =
      event.first == 0 ? 1 : event.first / event.rows + 1;

    const newSortField = event.sortField;
    const resetSorting = event.sortOrder > 0 && !this.sortAscending
    if (this.sortField === newSortField && resetSorting) {
      this.sortField = "debtorName";
      this.sortAscending = true;
      this.closedCasesTable.sortField = this.sortField;
    }
    else {
      this.sortField = event.sortField;
      this.sortAscending = event.sortOrder > 0
    }

    this.onGenerateReport();
  }

  onExportExcelClick() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    if (this.startDate == undefined) {
      this.notifications.errorKey('Errors.Reports.StartDateNotSelected');
      return false;
    }
    if (this.endDate == undefined) {
      this.notifications.errorKey('Errors.Reports.EndDateNotSelected');
      return false;
    }

    const parameters = this.getReportParameters();
    this.isLoading = true;
    this.reportsService.exportClosedCasesForExcel(parameters, this.translateService.getDefaultLang())
      .pipe(finalize(() => { this.isLoading = false; }), takeUntil(this.unsubscribe$))
      .subscribe(
        blob => {
          saveAs(blob, `${this.translateService.instant('Reports.ClosedCases')}_${new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6)}.xlsx`);
        },
        error => {
          this.notifications.genericError();
        });
  }

  openCaseInNewTab(caseModel: IClosedCaseReportResult) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/cases/${caseModel.caseNumber}`])
    );
    window.open(url, '_blank');
  }

  private translateStatusText(languageCode: string) {
    this.closedCases = this.closedCases.map(x => {
      x.statusTextDisplay = languageCode.equalsIgnoringCase('en') ? x.statusTextEn : x.statusText;
      return x;
    });
  }

  private getSortField(sortField: string) {
    if (sortField?.includes('status')) {
      return this.languageCode.equalsIgnoringCase('en') ? "StatusTextEn" : "StatusText"
    }
    return sortField
  }
}
